import { css } from '@emotion/react'
import { Button, colors, font } from 'plume-ui'
import { useState, useEffect } from 'react'
import { useUser } from '../../../../../contexts/AuthContext'
import { requestDownloadCompany } from '@/lib/api/company'
import { getCompanyDownloadInfo } from '@/lib/api/companyDownload'
import { printError } from '@/lib/log'
import { isEnterpriseWithTrial, isTrial } from '@/lib/user'
import {
  CompanyDownloadFormat,
  CompanyDownloadInfoData,
} from '../../../../../types/api'
import { IcomoonIcon } from '../../../../atoms/IcomoonIcon'
import { LoadableButton } from '../../../../atoms/LoadableButton'
import { Modal } from '../../../../atoms/Modal'
import { trackEvent } from '@/lib/tracking'

type Props = {
  shortname: string
  name: string
  isOpen: boolean
  children?: React.ReactNode
  onClose: () => void
}

export const CompanyDownloadModal: React.FC<Props> = (props) => {
  const user = useUser()

  const [downloadInfo, setDownloadInfo] =
    useState<CompanyDownloadInfoData | null>(null)

  useEffect(() => {
    getCompanyDownloadInfo()
      .then((res) => setDownloadInfo(res))
      .catch((err) => printError(err))
  }, [])

  if (!downloadInfo) {
    return null
  }

  // See https://github.com/Japan-Venture-Research/penguin/pull/813
  switch (true) {
    case isTrial(user):
      return <TrialModal {...props} />
    case downloadInfo.payPerUse:
      return <PayPerUseModal {...props} />
    case isEnterpriseWithTrial(user) && downloadInfo.allowed:
      return <NormalModal {...props} />
    default:
      return <NotDownloadbleModal {...props} />
  }
}

const TrialModal: React.FC<Props> = (props) => {
  return (
    <DownloadModalTemplate {...props}>
      <div
        css={css`
          margin-top: 25px;
          font-size: 24px;
        `}
      >
        トライアル制限
      </div>
      <div
        css={css`
          color: ${colors.gray70};
          margin-top: 40px;
        `}
      >
        こちらの機能は本契約ユーザーの方のみ利用可能となっています。
      </div>
      <div
        css={css`
          margin-top: 80px;
          margin-bottom: 50px;
        `}
      >
        <a href="https://s3-ap-northeast-1.amazonaws.com/entrepedia-public/samples/company_sample.pdf">
          <Button>サンプルをダウンロード</Button>
        </a>
      </div>
    </DownloadModalTemplate>
  )
}

const NotDownloadbleModal: React.FC<Props> = (props) => {
  return (
    <DownloadModalTemplate {...props}>
      <div
        css={css`
          color: ${colors.gray70};
          margin-top: 60px;
          font-size: 16px;
          line-height: 1.5;
        `}
      >
        <div>企業情報ダウンロードには契約プランの変更が必要です。</div>
        <div>
          お問い合わせ:
          <a
            href="https://initial.inc/contact"
            target="_blank"
            css={css`
              color: ${colors.gray70};
            `}
          >
            https://initial.inc/contact
          </a>
        </div>
      </div>
      <div
        css={css`
          margin-top: 80px;
          margin-bottom: 50px;
        `}
      >
        <a href="https://s3-ap-northeast-1.amazonaws.com/entrepedia-public/samples/company_sample.pdf">
          <Button>サンプルをダウンロード</Button>
        </a>
      </div>
    </DownloadModalTemplate>
  )
}

const PayPerUseModal: React.FC<Props> = (props) => {
  const [agreed, setAgreed] = useState(false)

  if (agreed) {
    return <NormalModal {...props} />
  }

  return (
    <DownloadModalTemplate {...props}>
      <CompanyName>{props.name}</CompanyName>
      <div
        css={css`
          margin-top: 24px;
          color: ${colors.gray70};
          font-size: 18px;
          text-align: center;
          line-height: 1.5;
        `}
      >
        <div>
          一件ダウンロードごとに
          <span
            css={css`
              font-weight: ${font.weight.bold};
              color: ${colors.black10};
            `}
          >
            750円（税別）
          </span>
          が課金されます。
        </div>
        <div>料金は、契約更新時にまとめてご請求いたします。</div>
      </div>
      <div
        css={css`
          margin-top: 30px;
          margin-bottom: 10px;
          font-size: 20px;
          color: ${colors.textStrong};
          text-decoration: none;
        `}
      >
        <IcomoonIcon
          name="info2"
          css={css`
            margin-right: 5px;
          `}
        />
        企業情報のダウンロードについて
      </div>
      <div
        css={css`
          font-size: 14px;
          color: ${colors.gray70};
          line-height: 1.3;
          white-space: pre-wrap;
        `}
      >
        <ul
          css={css`
            padding: 15px;

            li {
              margin-top: 5px;
            }
          `}
        >
          <li>
            ファイル形式は、ExcelとPDFから選択できます。ただし、同企業のダウンロードであっても、Excelファイル、PDFファイルをそれぞれ１回ずつダウンロードした場合は２件とカウントします。
          </li>
          <li>
            企業によっては情報が少ない場合があります。ダウンロードする前に、画面上で必ず内容をご確認ください。
          </li>
          <li>
            ダウンロードしたファイル、および、掲載データのコピー、転送等は規約で禁止しています。内部資料の範囲で利用ユーザ様ご本人がご利用ください。
          </li>
        </ul>
      </div>
      <Button
        fullwidth
        css={css`
          margin: 15px 0;
        `}
        onClick={() => setAgreed(true)}
      >
        同意して次へ進む
      </Button>
    </DownloadModalTemplate>
  )
}

type Status = 'loading' | 'requested' | 'error'

const NormalModal: React.FC<Props> = (props) => {
  const [status, setStatus] = useState<Status | null>(null)
  const [requestedFormat, setRequestedFormat] = useState<'pdf' | 'xlsx' | null>(
    null
  )
  const user = useUser()

  useEffect(() => {
    setStatus(null)
  }, [props.shortname])

  const handleRequestDownload = async (format: CompanyDownloadFormat) => {
    setStatus('loading')
    setRequestedFormat(format)
    try {
      await requestDownloadCompany(props.shortname, format)
      trackEvent(user, '企業個別のダウンロードリクエスト')
      setStatus('requested')
    } catch (e) {
      setStatus('error')
      printError(e)
    }
  }

  return (
    <DownloadModalTemplate {...props}>
      {status === 'requested' ? (
        <>
          <CompanyName>{props.name}</CompanyName>
          <div
            css={css`
              margin: 30px auto 10px;
            `}
          >
            ダウンロードのリクエストを送信しました
          </div>
          <div
            css={css`
              margin: 5px auto 10px;
              font-size: 16px;
              color: ${colors.blueGray50};
            `}
          >
            {requestedFormat === 'pdf' ? 'PDF' : 'Excel'}形式
          </div>
          <p
            css={css`
              margin: 10px 40px;
              font-size: 13px;
              line-height: 1.75;
              color: ${colors.textLight};
            `}
          >
            ダウンロードの準備完了後（数分後）、メールとスピーダ上の「お知らせ」にて通知します。30分経過しても通知がない場合は、サポートデスクまでご連絡ください。
          </p>
          <img
            alt="notification-highlight"
            src={`/static/images/companies/download/notification-highlight.png`}
            css={css`
              display: block;
              max-width: 385px;
              margin-block-start: 24px;
              margin-inlie: auto;
            `}
          />
          <Button
            size="small"
            css={css`
              margin-top: 10px;
            `}
            onClick={props.onClose}
          >
            閉じる
          </Button>
        </>
      ) : (
        <>
          <CompanyName>{props.name}</CompanyName>
          <p
            css={css`
              margin: 24px auto 0;
              max-width: 527px;
              font-size: 14px;
              line-height: 1.5;
              letter-spacing: 0.02em;
              color: ${colors.text};
            `}
            data-test-id="download-modal-description"
          >
            ご希望のファイルの形式の［ダウンロード］をクリックすると、ダウンロードの準備が始まります。
            <br />
            なお、ダウンロードファイルの生成に、数分かかります。
          </p>

          {status === 'error' && (
            <div
              css={css`
                margin-top: 10px;
                color: ${colors.red50};
              `}
            >
              企業データダウンロード中にエラーが発生しました。
            </div>
          )}
          <div
            css={css`
              display: flex;
              align-items: baseline;
              grid-column-gap: 32px;
              margin: 40px 0 10px;
            `}
          >
            <LoadableButton
              size="small"
              css={css`
                width: 200px;
              `}
              onClick={() => handleRequestDownload('pdf')}
              isLoading={status === 'loading' && requestedFormat === 'pdf'}
            >
              <IcomoonIcon
                name="download"
                css={css`
                  margin-right: 4px;
                `}
              />
              PDFをダウンロード
            </LoadableButton>
            <LoadableButton
              size="small"
              css={css`
                width: 200px;
              `}
              onClick={() => handleRequestDownload('xlsx')}
              isLoading={status === 'loading' && requestedFormat === 'xlsx'}
            >
              <IcomoonIcon
                name="download"
                css={css`
                  margin-right: 4px;
                `}
              />
              Excelをダウンロード
            </LoadableButton>
          </div>
        </>
      )}
    </DownloadModalTemplate>
  )
}

const DownloadModalTemplate: React.FC<Props> = (props) => {
  return (
    <Modal
      isHidden={!props.isOpen}
      onClose={props.onClose}
      css={css`
        max-width: 640px;
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
        `}
      >
        <div
          css={css`
            color: ${colors.textLight};
            display: flex;
          `}
        >
          <p
            css={css`
              margin: 0;
              font-size: 14px;
              line-height: 1.5;
              letter-spacing: 0.02em;
            `}
          >
            企業情報のダウンロード
          </p>
        </div>
        {props.children}
      </div>
    </Modal>
  )
}

const CompanyName: React.FC<{ children: string }> = (props) => {
  return (
    <p
      css={css`
        text-align: center;
        font-weight: ${font.weight.bold};
        font-size: 18px;
        line-height: 1.5;
        letter-spacing: 0.02em;
        margin: 2px auto 0;
      `}
    >
      {props.children}
    </p>
  )
}
